/**
 * Where a card goes while it is dragged across the board.
 *
 * The board is a set of columns (status → card ids, top to bottom). dnd-kit
 * reports what the dragged card is over — another card, or a column's empty
 * card area — and these functions turn that into the board the user should be
 * looking at, so cards make room as the dragged one passes. Pure, so the
 * routing is testable without simulating a pointer.
 */

export type BoardColumns = Record<string, number[]>;

/** The column holding `cardId`, if any. */
export function columnOf(columns: BoardColumns, cardId: number): string | undefined {
  return Object.keys(columns).find((status) => columns[status].includes(cardId));
}

/**
 * The board with `cardId` moved to where it is being held.
 *
 * @param over       the card id or column key under the dragged card
 * @param overColumn whether `over` is a column's card area rather than a card
 * @param below      the dragged card's middle is past the middle of `over`,
 *                   so it goes after that card rather than before it
 */
export function moveCard(
  columns: BoardColumns,
  cardId: number,
  over: number | string,
  overColumn: boolean,
  below: boolean
): BoardColumns {
  const from = columnOf(columns, cardId);
  const to = overColumn ? String(over) : columnOf(columns, Number(over));
  if (!from || !to || !(to in columns)) return columns;
  if (!overColumn && over === cardId) return columns;

  const source = columns[from].filter((id) => id !== cardId);
  const target = from === to ? source : [...columns[to]];

  let index: number;
  if (overColumn) {
    // Over the card area itself rather than a card: the space below the last
    // card, so the end of the column.
    index = target.length;
  } else {
    const overIndex = target.indexOf(Number(over));
    index = overIndex === -1 ? target.length : overIndex + (below ? 1 : 0);
  }
  target.splice(index, 0, cardId);

  if (from === to) {
    const unchanged = target.every((id, i) => id === columns[from][i]);
    return unchanged ? columns : { ...columns, [from]: target };
  }
  return { ...columns, [from]: source, [to]: target };
}
